import { createStyles, makeStyles, Theme } from "@material-ui/core";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import Close from "@material-ui/icons/Close";
import { TagCloud } from "react-tagcloud";
import {
	EMPTY_MESSAGES,
	EMPTY_MESSAGE_IMAGES,
	FONTS,
	PRIMARY_COLOR,
	SECONDARY_COLOR,
	THIRD_COLOR,
} from "../../constants";
import { addAlpha } from "../../helpers/DataHelpers";
import { formatNumber } from "../../helpers/FormatNumber";
import { visualizationDataType } from "../../MachineLearning/Dataset";
import { stateType, stateTypeF } from "../../state";
import { cloudDataType, topicCloudDataType } from "../NLP/textProcessor";
import { EmptyMessage } from "../Common/EmptyMessage";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		text: {
			margin: "3px",
			padding: "3px",
			display: "inline-block",
			border: "solid",
			borderColor: "rgba(0,0,0,0)",

			fontFamily: FONTS,
			cursor: "pointer",
			transition: "0.4s",
			"&:hover": { border: "solid" },
		},
		popupText: {
			fontFamily: FONTS,
			marginLeft: "20px",
			marginBottom: "10px",
			fontSize: "15px",
		},
		arrowLeft: {
			"&:hover": { transform: "scale(1.2)" },
			transition: "0.2s",
			width: "17px",
			cursor: "pointer",
		},
		arrowRight: {
			"&:hover": { transform: "scale(1.2) rotate(180deg)" },
			transform: "rotate(180deg)",
			width: "17px",
			marginRight: "8px",
			transition: "0.2s",
			cursor: "pointer",
		},
		close: {
			"&:hover": { transform: "scale(1.1)" },
			transition: "0.2s",
			width: "22px",
			cursor: "pointer",
		},
	})
);

type cloudChartType = {
	state: stateTypeF;
	visData: visualizationDataType;
	isPos: boolean;
};

const customRenderer = (tag, size, color, classes, sentiment, colorBySent) => {
	if (colorBySent) {
		color = "#FF0000";
		if (Math.abs(sentiment) <= 10) {
			color = PRIMARY_COLOR;
		} else if (sentiment > 0) {
			color = "#00FF00";
		}
	}

	return (
		<span
			key={tag.value}
			className={classes.text}
			style={{
				color: colorBySent
					? addAlpha(color, Math.max(Math.abs(sentiment), 0.5))
					: color,
				fontSize: `${size / 1.2}px`,
			}}
		>
			{tag.value}
		</span>
	);
};

export function CloudChart({ state, visData, isPos }: cloudChartType) {
	const classes = useStyles();
	if (visData === undefined) {
		return <div></div>;
	}
	let topicCloudData = visData.negTopicCloudData;
	if (isPos) {
		topicCloudData = visData.posTopicCloudData;
	}
	const tct = isPos ? "Positive" : "Negative";
	if (topicCloudData === undefined || topicCloudData.cloudData.length === 0) {
		return (
			<EmptyMessage
				image={EMPTY_MESSAGE_IMAGES.noData}
				message1={EMPTY_MESSAGES.empty1}
				message2={`No ${tct} Topics Found.`}
				button={false}
				marginTop={"30px"}
			></EmptyMessage>
		);
	}

	const options = {
		luminosity: "medium",
		hue: isPos ? "#00FF00" : THIRD_COLOR,
	};
	function handleClick(tag, size, color) {
		state.setTagClicked(tag.value);
		if (isPos) {
			state.setIsPosCloudOpen(true);
		} else {
			state.setIsNegCloudOpen(true);
		}
	}

	return (
		<div
			style={{
				textAlign: "center",
				justifyContent: "center",
				textOverflow: "clip",
				width: "100%",
				whiteSpace: "normal",
				marginRight: "0px",
				verticalAlign: "middle",
				height: "100%",
			}}
		>
			<div
				style={{
					fontFamily: FONTS,
					marginBottom: "20px",
					fontSize: "25px",
					fontWeight: "bold",
					marginTop: "-42px",
					marginLeft: "50px",
					marginRight: "50px",
					verticalAlign: "middle",
				}}
			></div>

			<TagCloud
				minSize={20}
				maxSize={50}
				shuffle={true}
				colorOptions={options}
				renderer={(tag, size, color) =>
					customRenderer(
						tag,
						size,
						color,
						classes,
						topicCloudData.topicSentiment,
						false
					)
				}
				randomSeed={1988}
				tags={topicCloudData.cloudData}
				onClick={(tag, size, color) => handleClick(tag, size, color)}
			/>
			<CloudPopUp
				state={state}
				isPos={isPos}
				cloudData={topicCloudData}
			></CloudPopUp>
		</div>
	);
}
type cloudInputType = {
	state: stateTypeF;
	isPos: boolean;
	cloudData: topicCloudDataType;
};
function CloudPopUp({ state, isPos, cloudData }: cloudInputType) {
	const classes = useStyles();

	let docs = cloudData.topicDocs[state.tagClicked] as string[];

	const appearsIn = cloudData.topicDocsCounts[state.tagClicked];
	let pageSelected = state.cloudPopupPageSelected;

	if (docs === undefined) {
		return <div></div>;
	}

	let doc = "";
	if (pageSelected >= docs.length) {
		pageSelected = docs.length - 1;
		state.setCloudPopupPageSelected(pageSelected);
	} else if (pageSelected < 0) {
		pageSelected = 0;
		state.setCloudPopupPageSelected(pageSelected);
	}
	doc = docs[pageSelected];

	function handleArrowClick(increment: number) {
		let newPage = state.cloudPopupPageSelected + increment;
		let nDocs = docs.length;
		newPage = Math.max(Math.min(newPage, nDocs - 1), 0);
		state.setCloudPopupPageSelected(newPage);
	}

	let sentiment = cloudData.topicSentiment[state.tagClicked];
	let sentimentAbs = Math.abs(sentiment);
	let label = "";
	if (sentimentAbs <= 10) {
		label = "Neutral";
	} else if (sentimentAbs <= 30) {
		label = sentiment > 0 ? "Moderately Positive" : "Moderately Negative";
	} else if (sentimentAbs <= 60) {
		label = sentiment > 0 ? "Positive" : "Negative";
	} else {
		label = sentiment > 0 ? "Very Positive" : "Very Negative";
	}

	return (
		<Dialog
			open={isPos ? state.isPosCloudOpen : state.isNegCloudOpen}
			maxWidth="sm"
			onClose={() =>
				isPos
					? state.setIsPosCloudOpen(false)
					: state.setIsNegCloudOpen(false)
			}
			PaperProps={{
				style: {
					// backgroundColor: "white",
					// borderRadius: "20px",
					backgroundImage:
						"linear-gradient(to right top, #ffffff, #fdfdfd, #fbfbfb, #f9f9f9, #f7f7f7, #f7f7f7, #f7f7f7, #f7f7f7, #f9f9f9, #fbfbfb, #fdfdfd, #ffffff)",
				},
			}}
		>
			<DialogContent
				style={{
					display: "flex",
					flexDirection: "row",
					overflow: "hidden",
				}}
			>
				<div
					style={{
						width: "400px",
						marginTop: "-30px",
						marginLeft: "-30px",
						marginRight: "-30px",
						color: "rgba(0,0,0,0.7)",
					}}
				>
					<div
						style={{
							fontFamily: FONTS,
							marginTop: "20px",
							fontSize: "25px",
							marginLeft: "20px",
							marginBottom: "30px",
							fontWeight: "bold",
							color: "black",
							display: "flex",
						}}
					>
						<div
							style={{
								width: "300px",
							}}
						>
							Topic:{" "}
							<span style={{ color: SECONDARY_COLOR }}>
								{" "}
								'{state.tagClicked}'
							</span>
						</div>
						<Close
							style={{
								marginRight: "20px",
								marginLeft: "auto",
								marginTop: "-3px",
								color: "rgba(0,0,0,0.5)",
							}}
							className={classes.close}
							onClick={() => {
								isPos
									? state.setIsPosCloudOpen(false)
									: state.setIsNegCloudOpen(false);
								state.setCloudPopupPageSelected(0);
							}}
						></Close>
					</div>
					<div
						className={classes.popupText}
						style={{ marginTop: "-20px" }}
					>
						<span>Appears in:</span>{" "}
						{`${formatNumber(appearsIn)} documents`}
					</div>
					{/*
					<div className={classes.popupText}>
						<span>Sentiment:</span> {Math.round(sentiment)} ({label}
						)
					</div>
					*/}

					<div
						style={{
							fontFamily: FONTS,
							marginLeft: "23px",
							fontSize: "20px",
							marginTop: "50px",
							fontWeight: "bold",
							display: "flex",
							color: "black",
							justifyContent: "space-between",
						}}
					>
						<div>Example Documents</div>
						<div style={{ marginRight: "20px", display: "flex" }}>
							<div
								style={{
									fontSize: "15px",
									lineHeight: "25px",
									marginRight: "5px",
								}}
							>
								{`${formatNumber(
									state.cloudPopupPageSelected + 1
								)} of ${formatNumber(docs.length)} `}
							</div>
							<ArrowForwardIosIcon
								className={classes.arrowRight}
								onClick={() => handleArrowClick(-1)}
							></ArrowForwardIosIcon>
							<ArrowForwardIosIcon
								className={classes.arrowLeft}
								onClick={() => handleArrowClick(1)}
							></ArrowForwardIosIcon>
						</div>
					</div>

					<div
						style={{
							marginTop: "5px",
							marginLeft: "20px",
							marginBottom: "10px",
							marginRight: "20px",
							overflowY: "scroll",
							height: "170px",
							// backgroundColor: "rgba(0,0,0,0.05)",
							border: "solid",
							borderRadius: "5px",
						}}
					>
						<div
							style={{
								margin: "10px",
								marginTop: "5px",
								fontFamily: FONTS,
								lineHeight: "23px",
								// color: "black",
								fontWeight: 200,
								color: "rgba(0,0,0,0.8)",

								height: "160px",
							}}
						>
							{doc}
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}
